import type { IconName } from '../components/ui'
import { ASSISTANT_ENABLED, EXPERIMENTS_ENABLED } from './features'

/**
 * The console's destinations, in one place.
 *
 * Two lists, because a signed-in account has two kinds of destination and only
 * one of them is conditional. `PRODUCT_NAV` is everybody's — the screens this
 * product exists for. `ADMIN_NAV` is an operator's, and §5.8 says a non-admin
 * must not see it at all.
 *
 * The rail and the phone drawer render from these same lists -- they are two
 * presentations of one navigation, not two navigations that have to be kept in
 * step. `badge` names a count the shell resolves at render time (the approval
 * queue's, today).
 */
export interface NavItem {
  to: string
  label: string
  /** Shown as a count beside the label when non-zero. */
  badge?: 'pendingCount'
  /** The glyph beside the label. Decoration: `Icon` is always `aria-hidden`. */
  icon?: IconName
  /** The group heading this destination sits under. Consecutive items share one. */
  section?: string
  /** A "New" tag beside the label, for a destination's first release. */
  tag?: 'new'
}

/**
 * State (Sprint 1.9.9 S6), the one CONDITIONAL product destination.
 *
 * Its route always exists, and the page renders NotFound for a reading with no
 * state map; the entry is shown only when `hasStateMap` says the reading has
 * one, so a user whose flags are off is never offered a page that is not there.
 * The glyph is `target`: the canvas draws a compass, which the icon set does
 * not have yet — an owner item in `docs/1.9.9-fidelity.md` row #1.
 */
export const STATE_NAV: NavItem = { to: '/state', label: 'State', icon: 'compass', section: 'Your brain' }

/**
 * The product surfaces (Sprint 1.9).
 *
 * **This list grows with the sprint.** A destination appears here when its route
 * exists, not when it is planned: a nav entry pointing at a route that resolves
 * to "page not found" is worse than a missing entry, because it looks like a
 * fault in the app rather than like work in progress. History joined it in Stage 6;
 * its detail view is a child route and deliberately has no entry of its own.
 * A new product destination joins one of the sections below and carries an
 * icon; `SideNav` groups CONSECUTIVE items by `section`, so order is the
 * grouping.
 *
 * Home joined it first in Sprint 1.9.1, in the same commit as its route and as
 * the two landing redirects -- the rule above is why, and `nav.test.ts` now
 * enforces it. Today became Report in Sprint 1.9.9 (R40): the entry moved with
 * its route, and `/today` is now only a redirect, which this list never names.
 */
const PRODUCT_DESTINATIONS: readonly NavItem[] = [
  { to: '/home', label: 'Home', icon: 'home', section: 'Your brain' },
  { to: '/report', label: 'Report', icon: 'sun', section: 'Your brain' },
  STATE_NAV,
  { to: '/record', label: 'Record', icon: 'record', section: 'Your brain' },
  { to: '/check-in', label: 'Check-in', icon: 'target', section: 'Your brain' },
  { to: '/baseline', label: 'Baseline', icon: 'target', section: 'Your brain' },
  { to: '/history', label: 'History', icon: 'clock', section: 'Your brain' },
  ...(EXPERIMENTS_ENABLED ? [{ to: '/experiments', label: 'Experiments', icon: 'pen', section: 'Product', tag: 'new' } satisfies NavItem] : []),
  { to: '/account', label: 'Account', icon: 'user', section: 'Account' },
] as const

/**
 * The assistant (agent-1-web W5), under its own "Help" group, before Account,
 * with a "New" tag for its first release (the tag goes in the next sprint).
 *
 * NOT in `PRODUCT_DESTINATIONS`, and spliced in only behind the module-scope
 * flag: with the flag off nothing references this object, so the bundle carries
 * neither the label nor the route (`assistantDark.test.ts`). Its route is
 * declared in `App.tsx` behind the same flag, in the same commit (the nav rule
 * above). The label is "Assistant" because the backend's guide topic for this
 * route is headed `# Assistant` (agent-1 C42).
 */
const ASSISTANT_NAV: NavItem = { to: '/assistant', label: 'Assistant', icon: 'message', section: 'Help', tag: 'new' }

function withAssistant(items: readonly NavItem[]): readonly NavItem[] {
  if (!ASSISTANT_ENABLED) return items
  const account = items.filter((item) => item.section === 'Account')
  return [...items.filter((item) => item.section !== 'Account'), ASSISTANT_NAV, ...account]
}

/** Everybody's destinations: every product surface but the conditional one. */
export const PRODUCT_NAV: readonly NavItem[] = withAssistant(PRODUCT_DESTINATIONS.filter((item) => item !== STATE_NAV))

const PRODUCT_NAV_WITH_STATE: readonly NavItem[] = withAssistant(PRODUCT_DESTINATIONS)

/**
 * The product list the shell renders: `PRODUCT_NAV`, with State third when the
 * reading on `/home` carries a state map (1.9.6 §3.1, 1.9.9 P6).
 *
 * Declared once in its place and filtered out, rather than spliced in by
 * index, so the order on screen is the order written above and `PRODUCT_NAV`
 * keeps its unconditional meaning.
 */
export function productNav({ state, experiments = false }: { state: boolean; experiments?: boolean }): readonly NavItem[] {
  const items = state ? PRODUCT_NAV_WITH_STATE : PRODUCT_NAV
  return experiments ? items : items.filter((item) => item.to !== '/experiments')
}

/**
 * NO ICONS AND NO SECTION, deliberately (R24).
 *
 * The operator's list is a second landmark, not a third group inside the first,
 * and v3 draws it without glyphs. An icon here would also cost a `nav__section`
 * heading to sit under, which is the one thing `AppShell.test.tsx:78-83` says
 * the two landmarks must not grow.
 */
export const ADMIN_NAV: readonly NavItem[] = [
  { to: '/admin/queue', label: 'Approval queue', badge: 'pendingCount' },
  { to: '/admin/users', label: 'Users' },
  { to: '/admin/audit', label: 'Audit log' },
  { to: '/admin/settings', label: 'Settings' },
] as const
